import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef } from "react";
import {
  aiFirstPrinciples,
  aiPipelineEvidence,
  getPillarEntry,
  PILLAR_EVIDENCE_ID,
  portfolioProfile,
  workflowStages,
} from "../content/portfolio";
import { navigate } from "../lib/navigation";
import { usePortfolioStore } from "../state/usePortfolioStore";
import type { Route } from "../types";
import {
  JourneyNavigation,
  type JourneyController,
} from "./JourneyNavigation";
import { ContactActions } from "./ContactActions";

interface DetailPanelProps {
  journey: JourneyController;
  route: Route;
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) ||
    target.getAttribute("role") === "textbox"
  );
}

function focusAndReveal(element: HTMLElement | null) {
  if (!element) return;

  element.focus({ preventScroll: true });
  element.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
}

function AIEngineeringPanel() {
  const branchRef = useRef<HTMLOListElement>(null);
  const evidenceRef = useRef<HTMLElement>(null);
  const selectedId = usePortfolioStore((state) => state.selectedAiSection);
  const selectSection = usePortfolioStore((state) => state.setAiSection);
  const selectStage = usePortfolioStore((state) => state.setWorkflowStage);
  const evidenceSelected = selectedId === aiPipelineEvidence.id;
  const selectedPrinciple =
    aiFirstPrinciples.find((principle) => principle.id === selectedId) ?? null;

  useEffect(() => {
    const branch = branchRef.current;
    const selected = branch?.querySelector<HTMLElement>(
      `[data-ai-section="${selectedId ?? "overview"}"]`,
    );
    if (!branch || !selected) return;
    branch.scrollTo({
      left: selected.offsetLeft - (branch.clientWidth - selected.offsetWidth) / 2,
    });
  }, [selectedId]);

  useEffect(() => {
    if (!evidenceSelected) return;
    focusAndReveal(evidenceRef.current);
  }, [evidenceSelected]);

  const openPipeline = () => {
    selectStage(workflowStages[0].id);
    selectSection("pipeline");
  };

  return (
    <>
      <div className="detail-heading">
        <p className="signal-label">{portfolioProfile.aiFirst.signal}</p>
        <h1>{portfolioProfile.aiFirst.title}</h1>
        <p>{portfolioProfile.aiFirst.summary}</p>
      </div>
      <ol
        ref={branchRef}
        className="pillar-branch ai-branch"
        aria-label="AI-first engineering principles and evidence"
      >
        <li>
          <button
            type="button"
            data-ai-section="overview"
            className={selectedId === null ? "is-active" : ""}
            onClick={() => selectSection(null)}
          >
            <span>00</span>
            Overview
          </button>
        </li>
        {aiFirstPrinciples.map((principle, index) => (
          <li key={principle.id}>
            <button
              type="button"
              data-ai-section={principle.id}
              className={
                principle.id === selectedPrinciple?.id ? "is-active" : ""
              }
              onClick={() => selectSection(principle.id)}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              {principle.label}
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            className={`is-evidence ${evidenceSelected ? "is-active" : ""}`}
            data-ai-section={aiPipelineEvidence.id}
            onClick={() => selectSection(aiPipelineEvidence.id)}
          >
            <span>Proof</span>
            {aiPipelineEvidence.label}
          </button>
        </li>
      </ol>
      {selectedPrinciple ? (
        <motion.article
          aria-live="polite"
          className="pillar-principle-focus ai-principle-focus"
          data-pillar="workflow"
          key={selectedPrinciple.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <small>AI-first engineering · {selectedPrinciple.label}</small>
          <h3>{selectedPrinciple.title}</h3>
          <p>{selectedPrinciple.description}</p>
        </motion.article>
      ) : evidenceSelected ? null : (
        <div className="pillar-principles">
          {aiFirstPrinciples.map((principle) => (
            <section key={principle.id}>
              <small>{principle.label}</small>
              <div>
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
              </div>
            </section>
          ))}
        </div>
      )}
      <article
        ref={evidenceRef}
        id="ai-first-evidence"
        className={`ai-pipeline-evidence ${
          evidenceSelected ? "is-selected" : ""
        }`}
        tabIndex={-1}
      >
        <small>Proof system · not the pillar itself</small>
        <h3>{aiPipelineEvidence.title}</h3>
        <p>{aiPipelineEvidence.description}</p>
        <button type="button" onClick={openPipeline}>
          Explore the eight-stage pipeline
          <span aria-hidden="true">→</span>
        </button>
      </article>
    </>
  );
}

function WorkflowPanel() {
  const stepsRef = useRef<HTMLOListElement>(null);
  const selectedId = usePortfolioStore((state) => state.selectedWorkflowStage);
  const selectStage = usePortfolioStore((state) => state.setWorkflowStage);
  const selectAiSection = usePortfolioStore((state) => state.setAiSection);
  const stage =
    workflowStages.find((candidate) => candidate.id === selectedId) ??
    workflowStages[0];
  const stageIndex = workflowStages.findIndex((item) => item.id === stage.id);
  const previousStage = workflowStages[stageIndex - 1] ?? null;
  const nextStage = workflowStages[stageIndex + 1] ?? null;
  const goPrevious = useCallback(() => {
    if (previousStage) selectStage(previousStage.id);
  }, [previousStage, selectStage]);
  const goNext = useCallback(() => {
    if (nextStage) selectStage(nextStage.id);
  }, [nextStage, selectStage]);

  useEffect(() => {
    const steps = stepsRef.current;
    const selected = steps?.querySelector<HTMLElement>(
      `[data-workflow-stage="${stage.id}"]`,
    );
    if (!steps || !selected) return;
    steps.scrollTo({
      left: selected.offsetLeft - (steps.clientWidth - selected.offsetWidth) / 2,
    });
  }, [stage.id]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      if (event.key === "ArrowLeft" && previousStage) {
        event.preventDefault();
        goPrevious();
      }
      if (event.key === "ArrowRight" && nextStage) {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goNext, goPrevious, nextStage, previousStage]);

  return (
    <>
      <div className="pipeline-context">
        <span>Local deep dive · autonomous PR pipeline</span>
        <button type="button" onClick={() => selectAiSection(null)}>
          <span aria-hidden="true">←</span>
          Return to AI-first engineering
        </button>
      </div>
      <div className="detail-heading">
        <p className="signal-label">{portfolioProfile.pipeline.signal}</p>
        <h1>{portfolioProfile.pipeline.title}</h1>
        <p>{portfolioProfile.pipeline.summary}</p>
      </div>
      <nav className="pipeline-navigation" aria-label="Pipeline stage navigation">
        <button
          type="button"
          aria-label="Previous pipeline stage"
          aria-keyshortcuts="ArrowLeft"
          disabled={!previousStage}
          onClick={goPrevious}
        >
          <span aria-hidden="true">←</span>
          Previous
        </button>
        <p aria-live="polite">
          <span>Local tour</span>
          <strong>
            Stage {stageIndex + 1} of {workflowStages.length}
          </strong>
        </p>
        <button
          type="button"
          aria-label="Next pipeline stage"
          aria-keyshortcuts="ArrowRight"
          disabled={!nextStage}
          onClick={goNext}
        >
          Next
          <span aria-hidden="true">→</span>
        </button>
      </nav>
      <ol
        ref={stepsRef}
        className="workflow-steps"
        aria-label="Autonomous PR pipeline stages"
      >
        {workflowStages.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              data-workflow-stage={item.id}
              className={item.id === stage.id ? "is-active" : ""}
              onClick={() => selectStage(item.id)}
            >
              <span>{item.label}</span>
              {item.title}
            </button>
          </li>
        ))}
      </ol>
      <motion.article
        aria-live="polite"
        className="stage-detail"
        key={stage.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <small>{stage.role}</small>
        <h3>{stage.title}</h3>
        <p>{stage.description}</p>
        <dl className="stage-contract">
          <div>
            <dt>Autonomy</dt>
            <dd>{stage.autonomy}</dd>
          </div>
          <div>
            <dt>Quality gate</dt>
            <dd>{stage.qualityGate}</dd>
          </div>
          <div>
            <dt>Human checkpoint</dt>
            <dd>{stage.humanCheckpoint}</dd>
          </div>
        </dl>
        <div className="stage-output">
          <span>Output</span>
          <strong>{stage.artifact}</strong>
        </div>
      </motion.article>
    </>
  );
}

function AIExperiencePanel() {
  const selectedAiSection = usePortfolioStore(
    (state) => state.selectedAiSection,
  );
  return selectedAiSection === "pipeline" ? (
    <WorkflowPanel />
  ) : (
    <AIEngineeringPanel />
  );
}

function PillarPanel({ slug }: { slug: string }) {
  const branchRef = useRef<HTMLOListElement>(null);
  const evidenceRef = useRef<HTMLElement>(null);
  const entry = getPillarEntry(slug);
  const selectedPillarPrinciples = usePortfolioStore(
    (state) => state.selectedPillarPrinciples,
  );
  const selectPrinciple = usePortfolioStore(
    (state) => state.setPillarPrinciple,
  );
  const selectedId = entry
    ? selectedPillarPrinciples[entry.id] ?? null
    : null;
  const evidenceSelected = selectedId === PILLAR_EVIDENCE_ID;
  const selectedPrinciple =
    entry?.principles.find((principle) => principle.id === selectedId) ?? null;

  useEffect(() => {
    const branch = branchRef.current;
    const selected = branch?.querySelector<HTMLElement>(
      `[data-pillar-principle="${selectedId ?? "overview"}"]`,
    );
    if (!branch || !selected) return;
    branch.scrollTo({
      left: selected.offsetLeft - (branch.clientWidth - selected.offsetWidth) / 2,
    });
  }, [selectedId]);

  useEffect(() => {
    if (!evidenceSelected) return;
    focusAndReveal(evidenceRef.current);
  }, [evidenceSelected]);

  if (!entry) return null;

  return (
    <>
      <div className="detail-heading">
        <p className="signal-label">{entry.label} pillar</p>
        <h1>{entry.title}</h1>
        <p>{entry.summary}</p>
      </div>
      <ol
        ref={branchRef}
        className="pillar-branch"
        aria-label={`${entry.label} principles and evidence`}
      >
        <li>
          <button
            type="button"
            data-pillar-principle="overview"
            className={selectedId === null ? "is-active" : ""}
            onClick={() => selectPrinciple(entry.id, null)}
          >
            <span>00</span>
            Overview
          </button>
        </li>
        {entry.principles.map((principle, index) => (
          <li key={principle.id}>
            <button
              type="button"
              data-pillar-principle={principle.id}
              className={
                principle.id === selectedPrinciple?.id ? "is-active" : ""
              }
              onClick={() => selectPrinciple(entry.id, principle.id)}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              {principle.label}
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            data-pillar-principle={PILLAR_EVIDENCE_ID}
            className={`is-evidence ${evidenceSelected ? "is-active" : ""}`}
            aria-controls={`${entry.id}-evidence`}
            aria-label={`View connected evidence: ${entry.evidence.title}`}
            onClick={() => selectPrinciple(entry.id, PILLAR_EVIDENCE_ID)}
          >
            <span>Proof</span>
            {entry.evidence.title}
          </button>
        </li>
      </ol>
      {selectedPrinciple ? (
        <motion.article
          aria-live="polite"
          className="pillar-principle-focus"
          data-pillar={entry.id}
          key={selectedPrinciple.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <small>
            {entry.label} · {selectedPrinciple.label}
          </small>
          <h3>{selectedPrinciple.title}</h3>
          <p>{selectedPrinciple.description}</p>
        </motion.article>
      ) : evidenceSelected ? null : (
        <div className="pillar-principles">
          {entry.principles.map((principle) => (
            <section key={principle.id}>
              <small>{principle.label}</small>
              <div>
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
              </div>
            </section>
          ))}
        </div>
      )}
      <article
        ref={evidenceRef}
        id={`${entry.id}-evidence`}
        className={`evidence-orbit ${evidenceSelected ? "is-selected" : ""}`}
        data-pillar={entry.id}
        tabIndex={-1}
      >
        <small>
          {evidenceSelected
            ? `${entry.label} evidence · Microsoft`
            : selectedPrinciple
            ? "Connected evidence · Microsoft"
            : "Selected evidence · Microsoft"}
        </small>
        <h3>{entry.evidence.title}</h3>
        <p>{entry.evidence.summary}</p>
        <dl className="outcome-lens">
          <div>
            <dt>Who it served</dt>
            <dd>{entry.evidence.audience}</dd>
          </div>
          <div>
            <dt>Product decision</dt>
            <dd>{entry.evidence.decision}</dd>
          </div>
        </dl>
        <p className="impact-line">
          <span>What changed</span>
          <strong>{entry.evidence.impact}</strong>
        </p>
      </article>
    </>
  );
}

function AboutPanel() {
  return (
    <>
      <div className="detail-heading">
        <p className="signal-label">{portfolioProfile.about.signal}</p>
        <h1>{portfolioProfile.about.title}</h1>
        <p>{portfolioProfile.about.summary}</p>
      </div>
      <dl className="career-record">
        {portfolioProfile.about.facts.map((fact) => (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
      <div className="principle-list">
        {portfolioProfile.about.themes.map((theme, index) => (
          <p key={theme.label}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{theme.label}</strong>
            <span className="principle-copy">{theme.description}</span>
          </p>
        ))}
      </div>
    </>
  );
}

function ContactPanel() {
  return (
    <>
      <div className="detail-heading">
        <p className="signal-label">{portfolioProfile.contact.signal}</p>
        <h1>{portfolioProfile.contact.title}</h1>
        <p>{portfolioProfile.contact.summary}</p>
      </div>
      <div className="contact-card">
        <span>Direct line</span>
        <ContactActions />
        <p>No form or queue. Your message opens in your own email app.</p>
      </div>
    </>
  );
}

export function DetailPanel({ journey, route }: DetailPanelProps) {
  const isDetail =
    route.kind === "workflow" ||
    route.kind === "pillar" ||
    route.kind === "about" ||
    route.kind === "contact";

  return (
    <AnimatePresence mode="wait">
      {isDetail ? (
        <motion.main
          id="main-content"
          className="detail-panel panel-surface"
          tabIndex={-1}
          key={
            route.kind === "pillar" ? `${route.kind}-${route.slug}` : route.kind
          }
          initial={{ opacity: 0, x: 40, filter: "blur(8px)" }}
          animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, x: 20, filter: "blur(4px)" }}
          transition={{ duration: 0.48, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="detail-toolbar">
            <button type="button" onClick={() => navigate("/")}>
              <span aria-hidden="true">←</span>
              Return to world
            </button>
            <button type="button" onClick={() => navigate("/index")}>
              Portfolio map
            </button>
          </div>
          <JourneyNavigation journey={journey} />
          {route.kind === "workflow" ? <AIExperiencePanel /> : null}
          {route.kind === "pillar" ? <PillarPanel slug={route.slug} /> : null}
          {route.kind === "about" ? <AboutPanel /> : null}
          {route.kind === "contact" ? <ContactPanel /> : null}
        </motion.main>
      ) : null}
    </AnimatePresence>
  );
}
